// react-magnetic-di - temporary comment, for auto dependency injection in tests
import { useCallback, useMemo } from 'react';

import { ON_INIT_CHANGE_ID } from '../../../../../../common/constants/constants';
import type { UiModificationsConfigSingleApp } from '../../../../../../common/types/apps';

import type { CustomBridgeInternalApi } from './types';
import { useOnBridgeError } from './use-on-bridge-error';
import { useOnChangeSubscription } from './use-on-change-subscription';
import { useOnErrorSubscription } from './use-on-error-subscription';
import { useOnInit } from './use-on-init';
import { useRegisterFields } from './use-register-fields';
import { useSubmit } from './use-submit';

type Props = {
	uiModifications: UiModificationsConfigSingleApp;
	onBridgeCommunicationReceived?: () => void;
	moduleScopes?: readonly string[];
	appId: string;
};

export const useCustomBridgeMethods = ({
	onBridgeCommunicationReceived,
	uiModifications,
	moduleScopes = [],
	appId,
}: Props) => {
	const { onInitPromise } = useOnInit({ moduleScopes });
	const subscribeToChanges = useOnChangeSubscription({
		uiModifications,
		moduleScopes,
	});

	const submit = useSubmit({ moduleScopes, appId });
	const registerFields = useRegisterFields({ appId });
	const onBridgeError = useOnBridgeError({ appId });
	const subscribeToErrors = useOnErrorSubscription({
		appId,
	});

	const getInternalAPI = useCallback((): CustomBridgeInternalApi => {
		onBridgeCommunicationReceived?.();
		return {
			data: {
				onInitChangeId: ON_INIT_CHANGE_ID,
				uiModifications,
				featureFlags: {
					// keep as true, we will also need to do the forge-bridge clean-up
					// and wait for some period of time so the users will migrate to the version without this FG.
					'forge-ui-modifications-screentabs_qoa33': true,
				},
			},
			actions: {
				getOnInitData: () => onInitPromise,
				subscribeToChanges,
				submit,
				registerFields,
				onBridgeError,
				subscribeToErrors,
			},
		};
	}, [
		onBridgeCommunicationReceived,
		uiModifications,
		subscribeToChanges,
		submit,
		registerFields,
		onInitPromise,
		onBridgeError,
		subscribeToErrors,
	]);

	const customBridgeMethods = useMemo(
		() => ({ getUiModificationsInternalAPI: getInternalAPI }),
		// TODO: Make sure that the `getInternalAPI` is not being regenerated on the consecutive renders
		// because the bridge consumes only the first version of it, if so clear the array deps to be [].
		[getInternalAPI],
	);

	return { customBridgeMethods };
};
